import * as React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormControlLabel, RadioGroup, Radio } from '@mui/material';
import Chip from '@mui/material/Chip';
import { Asset } from '../../../../store/Asset';
import { ProvisionalAsset } from '../../../../store/ProvisionalAsset';
import { ComponentSpinner } from '../../Loading/ComponentSpinner';
import { Text, TextBlock } from '../../../Atoms/Text';
import { PaymentFrequency } from '../../../../utils/constants';
import { chipStyles } from '../../../../utils/helpers';

interface PaymentFrequencyModalProps {
  open: boolean;
  onClose: () => void;
  data: Asset | ProvisionalAsset;
  onSave?: (data: Asset | ProvisionalAsset) => void;
}

const radioStyle = { height: '2rem', width: '2rem', m: '0 0.3rem' };

interface localDataInterface {
  paymentFrequency: PaymentFrequency;
  paymentFrequencyOverride: PaymentFrequency | '';
}

export const PaymentFrequencyModal = ({ open, onClose, data, onSave }: PaymentFrequencyModalProps) => {
  const [loading, setLoading] = useState(false);
  const [localData, setLocalData] = useState<localDataInterface>({} as localDataInterface);

  const handleSave = async () => {
    if (onSave) {
      setLoading(true);
      const updatedData = {
        ...data,
        paymentFrequency: localData.paymentFrequency,
        paymentFrequencyOverride: localData.paymentFrequencyOverride || null
      };
      await onSave(updatedData);
      setLoading(false);
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const getNewPaymentFrequency = (localData: localDataInterface) => {
    if (localData.paymentFrequencyOverride) {
      return localData.paymentFrequencyOverride;
    } else if ('supplierPaymentFrequency' in data && data.supplierPaymentFrequency) {
      return data.supplierPaymentFrequency;
    } else if (data.supplierPaymentFrequencyException) {
      return data.supplierPaymentFrequencyException;
    } else {
      return PaymentFrequency.MONTHLY;
    }
  };

  useEffect(() => {
    if (data) {
      const initialData = {
        paymentFrequency: data.paymentFrequency as PaymentFrequency,
        paymentFrequencyOverride: data.paymentFrequencyOverride
          ? (data.paymentFrequencyOverride as PaymentFrequency)
          : ''
      } as localDataInterface;

      initialData.paymentFrequency = getNewPaymentFrequency(initialData) as PaymentFrequency;

      setLocalData({
        ...initialData
      });
    }
  }, [data, data?.id, open]);

  useEffect(() => {
    setLocalData((prev) => ({
      ...prev,
      paymentFrequency: getNewPaymentFrequency(prev) as PaymentFrequency
    }));
  }, [localData.paymentFrequencyOverride]);

  const getPaymentFrequencyLabel = (value: PaymentFrequency | null) => {
    if (!value) return <Text color="grey3">None</Text>;
    return (
      <Chip
        label={value}
        size="small"
        sx={chipStyles(value)}
        title={value}
      />
    );
  };

  return (
    <Dialog
      maxWidth={'md'}
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: (event: React.SubmitEvent) => {
            event.preventDefault();
            handleSave();
          }
        }
      }}
      fullWidth>
      {!loading && <DialogTitle sx={{ fontSize: '1.25rem' }}>Payment Frequency</DialogTitle>}
      <DialogContent dividers>
        {loading && <ComponentSpinner label="Saving..." />}
        {!loading && (
          <div className="flex flex-row gap-x-10 w-full">
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">Payment Frequency</TextBlock>
              <TextBlock dataTestId="payment-frequency">
                {getPaymentFrequencyLabel(localData.paymentFrequency)}
              </TextBlock>
            </div>
            <div className="flex flex-col gap-y-3 w-1/2">
              <TextBlock color="grey6">Payment Frequency Override</TextBlock>
              <RadioGroup
                aria-label="assetField"
                name="assetField"
                value={localData.paymentFrequencyOverride}
                onChange={(e) => {
                  const val = e.target.value;
                  const value = !val ? '' : (val as PaymentFrequency);
                  setLocalData({
                    ...localData,
                    paymentFrequencyOverride: value
                  });
                }}
                className="!m-0 p-0">
                <FormControlLabel
                  value={PaymentFrequency.MONTHLY}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(PaymentFrequency.MONTHLY)}
                />
                <FormControlLabel
                  value={PaymentFrequency.QUARTERLY}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(PaymentFrequency.QUARTERLY)}
                />
                <FormControlLabel
                  value={PaymentFrequency.ANNUALLY}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(PaymentFrequency.ANNUALLY)}
                />
                <FormControlLabel
                  value={PaymentFrequency.ONE_TIME}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(PaymentFrequency.ONE_TIME)}
                />
                <FormControlLabel
                  value={''}
                  control={<Radio sx={radioStyle} />}
                  label={getPaymentFrequencyLabel(null)}
                />
              </RadioGroup>
              {'supplierPaymentFrequency' in data && (
                <>
                  <TextBlock color="grey6">Service Contract Payment Frequency</TextBlock>
                  <TextBlock dataTestId="service-contract-payment-frequency">
                    {getPaymentFrequencyLabel(data.supplierPaymentFrequency as PaymentFrequency)}
                  </TextBlock>
                </>
              )}
              <TextBlock color="grey6">Supplier Payment Frequency Exception</TextBlock>
              <TextBlock dataTestId="supplier-payment-frequency-exception">
                {getPaymentFrequencyLabel(data.supplierPaymentFrequencyException as PaymentFrequency)}
              </TextBlock>
              <TextBlock color="grey6">Default Payment Frequency</TextBlock>
              <TextBlock dataTestId="default-payment-frequency">
                {getPaymentFrequencyLabel(PaymentFrequency.MONTHLY)}
              </TextBlock>
            </div>
          </div>
        )}
      </DialogContent>
      {!loading && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            data-testid="cancel-button"
            onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained">
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};
